/* eslint-disable no-restricted-syntax */
import {
  SharesTransferExcelOperation,
  SharesTransferExcelValidationError,
  SharesTransferLedgerState,
} from '../types/SharesTransferExcel';

type LedgerRange = {
  from: number;
  to: number;
  shareClass: string;
  encumbrances?: unknown[];
};

const mergeAdjacentRanges = (ranges: LedgerRange[]): LedgerRange[] => {
  if (!ranges.length) {
    return [];
  }

  const sorted = [...ranges].sort(
    (firstRange, secondRange) =>
      firstRange.from - secondRange.from || firstRange.to - secondRange.to
  );

  const merged: LedgerRange[] = [{ ...sorted[0] }];

  for (let index = 1; index < sorted.length; index += 1) {
    const currentRange = sorted[index];
    const lastMergedRange = merged[merged.length - 1];

    if (currentRange.from <= lastMergedRange.to + 1) {
      lastMergedRange.to = Math.max(lastMergedRange.to, currentRange.to);
    } else {
      merged.push({ ...currentRange });
    }
  }

  return merged;
};

const cloneLedger = (
  ledger: SharesTransferLedgerState
): SharesTransferLedgerState => {
  const clone: SharesTransferLedgerState = {};

  Object.entries(ledger).forEach(([cif, ranges]) => {
    clone[cif] = mergeAdjacentRanges(ranges.map((range) => ({ ...range })));
  });

  return clone;
};

const getNormalizedPartnerRanges = (
  ranges: LedgerRange[] | undefined
): LedgerRange[] => {
  if (!ranges?.length) {
    return ranges || [];
  }

  return mergeAdjacentRanges(ranges);
};

const isRangeFullyCovered = (
  ranges: LedgerRange[],
  shareFrom: number,
  shareTo: number
): boolean => {
  const merged = mergeAdjacentRanges(
    ranges.filter((range) => range.to >= shareFrom && range.from <= shareTo)
  );

  let cursor = shareFrom;

  for (const range of merged) {
    if (range.from > cursor) {
      return false;
    }

    cursor = Math.max(cursor, range.to + 1);

    if (cursor > shareTo) {
      return true;
    }
  }

  return cursor > shareTo;
};

const removeCoveredRangeFromLedger = (
  ranges: LedgerRange[],
  shareFrom: number,
  shareTo: number,
  holdingClassId: string
): LedgerRange[] => {
  const result: LedgerRange[] = [];

  ranges.forEach((currentRange) => {
    if (currentRange.to < shareFrom || currentRange.from > shareTo) {
      result.push({ ...currentRange });
      return;
    }

    if (currentRange.from < shareFrom) {
      result.push({
        ...currentRange,
        to: shareFrom - 1,
      });
    }

    if (currentRange.to > shareTo) {
      result.push({
        ...currentRange,
        from: shareTo + 1,
      });
    }
  });

  return mergeAdjacentRanges(
    result.map((range) => ({
      from: range.from,
      to: range.to,
      shareClass: holdingClassId,
      encumbrances: range.encumbrances,
    }))
  );
};

const appendRange = (
  ranges: LedgerRange[] | undefined,
  range: LedgerRange
): LedgerRange[] => [...(ranges || []), { ...range }];

const applyOperationTransfer = ({
  ledger,
  operation,
  holdingClassId,
}: {
  ledger: SharesTransferLedgerState;
  operation: SharesTransferExcelOperation;
  holdingClassId: string;
}): SharesTransferLedgerState => {
  let nextLedger = ledger;

  operation.sellers.forEach((seller) => {
    nextLedger = {
      ...nextLedger,
      [seller.cif]: getNormalizedPartnerRanges(
        removeCoveredRangeFromLedger(
          nextLedger[seller.cif] || [],
          seller.shareFrom,
          seller.shareTo,
          holdingClassId
        )
      ),
    };
  });

  operation.buyers.forEach((buyer) => {
    nextLedger = {
      ...nextLedger,
      [buyer.cif]: getNormalizedPartnerRanges(
        appendRange(nextLedger[buyer.cif], {
          from: buyer.shareFrom,
          to: buyer.shareTo,
          shareClass: holdingClassId,
        })
      ),
    };
  });

  return nextLedger;
};

export type SimulateSharesTransferLedgerResult = {
  operations: SharesTransferExcelOperation[];
  errors: SharesTransferExcelValidationError[];
  totalErrors: number;
};

export const simulateSharesTransferLedger = ({
  operations,
  initialLedger,
  holdingClassId,
}: {
  operations: SharesTransferExcelOperation[];
  initialLedger: SharesTransferLedgerState;
  holdingClassId: string;
}): SimulateSharesTransferLedgerResult => {
  let ledger = cloneLedger(initialLedger);
  const ownershipErrors: SharesTransferExcelValidationError[] = [];
  const updatedOperations: SharesTransferExcelOperation[] = [];

  operations.forEach((operation) => {
    const operationOwnershipErrors: SharesTransferExcelValidationError[] = [];
    const nonMixedErrors = operation.errors.filter(
      (error) => error.code !== 'MIXED_OPERATION_TYPES'
    );
    const canSimulateTransfer =
      operation.isValid || nonMixedErrors.length === 0;

    if (!canSimulateTransfer) {
      updatedOperations.push(operation);
      return;
    }

    operation.sellers.forEach((seller) => {
      ledger = {
        ...ledger,
        [seller.cif]: getNormalizedPartnerRanges(ledger[seller.cif]),
      };

      const sellerRanges = ledger[seller.cif] || [];

      if (
        !isRangeFullyCovered(
          sellerRanges,
          seller.shareFrom,
          seller.shareTo
        )
      ) {
        operationOwnershipErrors.push({
          code: 'RANGE_NOT_AVAILABLE',
          operationId: operation.operationId,
          rowIndex: seller.rowIndex,
          cif: seller.cif,
          detail: `${seller.shareFrom}-${seller.shareTo}`,
        });

        ledger = {
          ...ledger,
          [seller.cif]: getNormalizedPartnerRanges(
            appendRange(ledger[seller.cif], {
              from: seller.shareFrom,
              to: seller.shareTo,
              shareClass: holdingClassId,
            })
          ),
        };
      }
    });

    ledger = applyOperationTransfer({ ledger, operation, holdingClassId });

    if (operationOwnershipErrors.length) {
      ownershipErrors.push(...operationOwnershipErrors);
      updatedOperations.push({
        ...operation,
        errors: [...operation.errors, ...operationOwnershipErrors],
        isValid: false,
      });
      return;
    }

    updatedOperations.push(operation);
  });

  const structuralErrors = operations.flatMap((operation) => operation.errors);

  return {
    operations: updatedOperations,
    errors: [...structuralErrors, ...ownershipErrors],
    totalErrors: [...structuralErrors, ...ownershipErrors].length,
  };
};

export {
  cloneLedger,
  applyOperationTransfer,
  mergeAdjacentRanges,
  isRangeFullyCovered,
  removeCoveredRangeFromLedger,
};
